<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Link2, Trash2 } from 'lucide-svelte';
  import { tasksApi } from '$lib/api/tasks';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { route } from '$lib/state/route.svelte';
  import { TASK_COLUMNS } from '$lib/types';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { AssignableUser, Task } from '$lib/types';

  const {
    projectId,
    task,
    defaultCol,
    members,
    onClose
  }: {
    projectId: number;
    task: Task | null;
    defaultCol?: string;
    members: AssignableUser[];
    onClose: () => void;
  } = $props();

  const queryClient = useQueryClient();
  const editing = !!task;

  const TAGS = ['', 'bug', 'api', 'firmware', 'infra', 'perf', 'ui'];

  let title = $state(task?.title ?? '');
  let desc = $state(task?.desc ?? '');
  let col = $state(task?.col ?? defaultCol ?? 'backlog');
  let tag = $state(task?.tag ?? '');
  let assignee = $state<number | ''>(task?.assignee_user_id ?? '');
  let formError = $state('');

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['project-tasks', projectId] });
    queryClient.invalidateQueries({ queryKey: ['project-tickets', projectId] });
  }

  const saveMutation = createMutation(() => ({
    mutationFn: async () => {
      const body = {
        title: title.trim(),
        desc,
        col,
        tag,
        assignee_user_id: assignee === '' ? null : assignee
      };
      return editing ? tasksApi.update(task!.id, body) : tasksApi.create(projectId, body);
    },
    onSuccess: () => {
      invalidate();
      onClose();
      toast.success(editing ? 'Task updated' : 'Task created');
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: () => tasksApi.remove(task!.id),
    onSuccess: () => {
      invalidate();
      onClose();
      toast.success('Task deleted');
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!title.trim()) {
      formError = 'Title is required';
      return;
    }
    formError = '';
    saveMutation.mutate();
  }

  function openTicket() {
    if (task?.from_ticket_id) {
      onClose();
      route.push({ tab: 'ticket', ticketId: task.from_ticket_id, back: route.current });
    }
  }
</script>

<Modal title={editing ? 'Edit task' : 'New task'} {onClose}>
  {#snippet footer()}
    {#if editing}
      <button type="button" class="btn btn-danger" onclick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
        <Trash2 size={15} />Delete
      </button>
    {/if}
    <span class="spacer"></span>
    <button type="button" class="btn btn-secondary" onclick={onClose}>Cancel</button>
    <button type="submit" form="task-form" class="btn btn-primary" disabled={!title.trim() || saveMutation.isPending}>
      {editing ? 'Save' : 'Create task'}
    </button>
  {/snippet}

  <form id="task-form" onsubmit={submit}>
    <div class="field">
      <label class="label" for="tk-title">Title</label>
      <input id="tk-title" class="input" placeholder="What needs doing?" bind:value={title} />
    </div>
    <div class="field">
      <label class="label" for="tk-desc">Description</label>
      <textarea id="tk-desc" class="textarea" placeholder="Details, acceptance notes…" bind:value={desc}></textarea>
    </div>
    <div style="display:flex;gap:12px">
      <div class="field" style="flex:1">
        <label class="label" for="tk-col">Column</label>
        <select id="tk-col" class="select" bind:value={col}>
          {#each TASK_COLUMNS as c (c.id)}
            <option value={c.id}>{c.name}</option>
          {/each}
        </select>
      </div>
      <div class="field" style="flex:1">
        <label class="label" for="tk-tag">Label</label>
        <select id="tk-tag" class="select" bind:value={tag}>
          {#each TAGS as t (t)}
            <option value={t}>{t || 'None'}</option>
          {/each}
        </select>
      </div>
    </div>
    <div class="field">
      <label class="label" for="tk-assignee">Assignee</label>
      <select id="tk-assignee" class="select" bind:value={assignee}>
        <option value="">Unassigned</option>
        {#each members as m (m.id)}
          <option value={m.id}>{m.name}</option>
        {/each}
      </select>
    </div>
    {#if task?.from_ticket_id}
      <div style="font-size:12.5px;color:var(--t3);display:flex;align-items:center;gap:7px">
        <Link2 size={14} />Linked to a ticket
        <button type="button" class="btn btn-ghost btn-sm" style="padding:3px 8px" onclick={openTicket}>Open ticket</button>
      </div>
    {/if}
    {#if formError}<p style="color:var(--danger);font-size:13px;margin-top:8px">{formError}</p>{/if}
  </form>
</Modal>
